import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { PageHeader, Toolbar, DataCard, StatusPill } from "@/components/admin/AdminPrimitives";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, ExternalLink, Filter } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/negocios")({
  head: () => ({ meta: [{ title: "Negócios — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: NegociosAdmin,
});

function NegociosAdmin() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | "published" | "draft" | "archived">("all");

  const list = useQuery({
    queryKey: ["admin.negocios", status],
    queryFn: async () => {
      let query = supabase
        .from("businesses")
        .select("id, title, slug, active, publication_status, opportunity_score, maturity_score, updated_at, category_id, categories(name)")
        .order("updated_at", { ascending: false })
        .limit(500);
      if (status !== "all") query = query.eq("publication_status", status);
      const { data } = await query;
      return data ?? [];
    },
  });

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list.data ?? [];
    return (list.data ?? []).filter((r: any) =>
      [r.title, r.slug, (r as any).categories?.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)),
    );
  }, [q, list.data]);

  return (
    <div>
      <PageHeader
        title="Negócios"
        description="Gerencie a biblioteca completa de fichas."
        actions={
          <>
            <button className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium text-[var(--navy)] hover:bg-[var(--gray-soft)]">
              Importar CSV
            </button>
            <button className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--navy)] px-3 py-2 text-sm font-semibold text-white hover:opacity-90">
              <Plus size={14} /> Novo negócio
            </button>
          </>
        }
      />

      <Toolbar search={q} onSearch={setQ} placeholder="Pesquisar por nome, slug ou categoria…">
        <div className="flex items-center gap-1 rounded-lg border border-black/[0.08] bg-white p-0.5 text-xs">
          {(["all", "published", "draft", "archived"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-md px-2.5 py-1.5 font-medium transition ${
                status === s ? "bg-[var(--navy)] text-white" : "text-[var(--gray-ink)] hover:text-[var(--navy)]"
              }`}
            >
              {s === "all" ? "Todos" : s === "published" ? "Publicados" : s === "draft" ? "Rascunhos" : "Arquivados"}
            </button>
          ))}
        </div>
        <button className="inline-flex h-9 items-center gap-1 rounded-lg border border-black/[0.08] bg-white px-2.5 text-xs text-[var(--gray-ink)] hover:text-[var(--navy)]">
          <Filter size={12} /> Filtros
        </button>
      </Toolbar>

      <DataCard>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--gray-soft)]/60 text-left text-[11px] uppercase tracking-wide text-[var(--gray-ink)]">
              <tr>
                <th className="px-4 py-3 font-semibold">Nome</th>
                <th className="px-4 py-3 font-semibold">Categoria</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Score</th>
                <th className="px-4 py-3 text-right font-semibold">Maturidade</th>
                <th className="px-4 py-3 font-semibold">Última edição</th>
                <th className="px-4 py-3 text-right font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/[0.05]">
              {list.isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="p-2"><Skeleton className="h-9 w-full" /></td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-sm text-[var(--gray-ink)]">
                    Nenhum negócio encontrado.
                  </td>
                </tr>
              ) : (
                rows.map((b: any) => (
                  <tr key={b.id} className="transition hover:bg-[var(--gray-soft)]/40">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-[var(--navy)]">{b.title}</p>
                      <p className="text-[11px] text-[var(--gray-ink)]">/{b.slug}</p>
                    </td>
                    <td className="px-4 py-3 text-[var(--gray-ink)]">{b.categories?.name ?? "—"}</td>
                    <td className="px-4 py-3">
                      <StatusPill
                        tone={b.publication_status === "published" ? "green" : b.publication_status === "draft" ? "gold" : "gray"}
                      >
                        {b.publication_status ?? "—"}
                      </StatusPill>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{Number(b.opportunity_score ?? 0).toFixed(1)}</td>
                    <td className="px-4 py-3 text-right text-[var(--gray-ink)]">{Number(b.maturity_score ?? 0).toFixed(0)}</td>
                    <td className="px-4 py-3 text-xs text-[var(--gray-ink)]">
                      {new Date(b.updated_at).toLocaleDateString("pt-BR")}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={"/membros/negocios/$slug" as any}
                        params={{ slug: b.slug } as any}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--navy)] hover:opacity-70"
                      >
                        Ver <ExternalLink size={11} />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-black/[0.05] bg-[var(--gray-soft)]/40 px-4 py-2.5 text-xs text-[var(--gray-ink)]">
          <span>{rows.length} registros</span>
          <span>Página 1 de 1</span>
        </div>
      </DataCard>
    </div>
  );
}
